import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export default function BookingCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit_id");
  const preselectedDate = searchParams.get("event_date") || searchParams.get("date") || "";
  const preselectedEndDate = searchParams.get("end_date") || preselectedDate;
  const [eventTypes, setEventTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    event_type_id: "",
    client_id: "",
    new_client: { name: "", phone: "", email: "" },
    create_new_client: false,
    event_date: "",
    end_date: "",
    event_start_time: "",
    event_end_time: "",
    location: "",
    assigned_employee_ids: [],
    special_instructions: "",
    client_note: "",
    custom_data: {},
  });

  useEffect(() => {
    Promise.all([
      api.get("/event-types"),
      api.get("/clients"),
      api.get("/team"),
    ]).then(([et, cl, tm]) => {
      setEventTypes(et.data);
      setClients(cl.data);
      setTeam(tm.data.filter((u) => u.status === "active"));
    });
  }, []);

  useEffect(() => {
    if (!editId && preselectedDate) {
      setForm((f) => ({ ...f, event_date: preselectedDate, end_date: preselectedEndDate || preselectedDate }));
      return;
    }
    if (!editId) return;
    api.get(`/bookings/${editId}`).then(({ data }) => {
      setForm((f) => ({
        ...f,
        event_type_id: data.event_type_id || "",
        client_id: data.client_id || "",
        event_date: data.event_date || "",
        end_date: data.end_date || "",
        event_start_time: data.event_start_time || "",
        event_end_time: data.event_end_time || "",
        location: data.location || "",
        assigned_employee_ids: data.assigned_employee_ids || [],
        special_instructions: data.special_instructions || "",
        client_note: data.client_note || "",
        custom_data: data.custom_data || {},
      }));
    });
  }, [editId, preselectedDate, preselectedEndDate]);

  useEffect(() => {
    if (form.event_type_id) {
      api.get("/custom-fields", { params: { applies_to: "event", event_type_id: form.event_type_id } })
        .then((r) => setCustomFields(r.data));
    } else {
      setCustomFields([]);
    }
  }, [form.event_type_id]);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const updateCD = (name) => (v) => setForm((f) => ({ ...f, custom_data: { ...f.custom_data, [name]: v?.target ? v.target.value : v } }));
const [clientOpen, setClientOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let clientId = form.client_id;
      if (form.create_new_client) {
        if (!form.new_client.name || !form.new_client.phone) {
          toast.error("New client requires name and phone");
          setSaving(false);
          return;
        }
        const { data } = await api.post("/clients", form.new_client);
        clientId = data.id;
      }
      if (!clientId) {
        toast.error("Please select or create a client");
        setSaving(false);
        return;
      }
      const payload = {
        event_type_id: form.event_type_id,
        client_id: clientId,
        event_date: form.event_date,
        end_date: form.end_date || null,
        event_start_time: form.event_start_time,
        event_end_time: form.event_end_time,
        location: form.location,
        assigned_employee_ids: form.assigned_employee_ids,
        special_instructions: form.special_instructions,
        client_note: form.client_note,
        custom_data: form.custom_data,
        status: "pending",
      };
      const { data } = editId
        ? await api.patch(`/bookings/${editId}`, payload)
        : await api.post("/bookings", payload);
      toast.success(editId ? "Booking updated" : "Booking created");
      navigate(`/app/bookings/${data.id}`);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (eventTypes.length === 0) {
    return (
      <div className="max-w-xl mx-auto bg-white border border-black/10 rounded-md p-8 text-center" data-testid="no-event-types-message">
        <h2 className="font-serif text-2xl tracking-tight">Create an event type first</h2>
        <p className="text-sm text-zinc-600 mt-2">Define at least one event type (e.g. Wedding Decoration, DJ Night) before booking events.</p>
        <Button className="mt-6 bg-[#1A1A1A] text-white hover:bg-black rounded-full" onClick={() => navigate("/app/event-types")} data-testid="goto-event-types-btn">
          Go to Event Types
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="booking-create-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">New booking</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">{editId ? "Edit booking" : "Create a booking"}</h1>
      </div>

      <form onSubmit={submit} className="bg-white border border-black/10 rounded-md p-6 md:p-8 space-y-6">
        <Section title="Event">
          <div>
            <Label>Event type *</Label>
            <Select value={form.event_type_id} onValueChange={update("event_type_id")}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="booking-event-type-select"><SelectValue placeholder="Select event type" /></SelectTrigger>
              <SelectContent>
                {eventTypes.filter(et => et.status === "active").map((et) => (
                  <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Start date *</Label>
              <Input required type="date" className="mt-1.5 h-11" value={form.event_date} 
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  event_date: value,
                  end_date: value, // auto-fill end date to match
                }));
              }}
              data-testid="booking-date-input" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" className="mt-1.5 h-11" value={form.end_date} onChange={update("end_date")} data-testid="booking-end-date-input" />
            </div>
            <div>
              <Label>Start time</Label>
              <Input type="time" className="mt-1.5 h-11" value={form.event_start_time} onChange={update("event_start_time")} data-testid="booking-start-input" />
            </div>
            <div>
              <Label>End time</Label>
              <Input type="time" className="mt-1.5 h-11" value={form.event_end_time} onChange={update("event_end_time")} data-testid="booking-end-input" />
            </div>
          </div>
          <div>
            <Label>Location</Label>
            <Input className="mt-1.5 h-11" value={form.location} onChange={update("location")} placeholder="Venue address" data-testid="booking-location-input" />
          </div>
        </Section>

        <Section title="Client">
          <div className="flex items-center gap-2">
            <Checkbox id="new-client" checked={form.create_new_client} onCheckedChange={(v) => setForm({ ...form, create_new_client: !!v })} data-testid="booking-new-client-checkbox" />
            <Label htmlFor="new-client" className="cursor-pointer">Create new client</Label>
          </div>
          {form.create_new_client ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input className="mt-1.5 h-11" value={form.new_client.name} onChange={(e) => setForm({ ...form, new_client: { ...form.new_client, name: e.target.value } })} data-testid="booking-new-client-name" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input className="mt-1.5 h-11" value={form.new_client.phone} onChange={(e) => setForm({ ...form, new_client: { ...form.new_client, phone: e.target.value } })} data-testid="booking-new-client-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input className="mt-1.5 h-11" value={form.new_client.email} onChange={(e) => setForm({ ...form, new_client: { ...form.new_client, email: e.target.value } })} data-testid="booking-new-client-email" />
              </div>
            </div>
          ) : (
            <div>
              <Label>Existing client *</Label>
              <Popover open={clientOpen} onOpenChange={setClientOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={clientOpen}
                    className="mt-1.5 h-11 w-full justify-between font-normal"
                    data-testid="booking-client-select"
                  >
                    {form.client_id
                      ? (() => {
                          const c = clients.find((c) => c.id === form.client_id);
                          return c ? `${c.name} · ${c.phone}` : "Select client";
                        })()
                      : clients.length
                      ? "Select client"
                      : "No clients yet"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search client..." />
                    <CommandList>
                      <CommandEmpty>No client found.</CommandEmpty>
                      <CommandGroup>
                        {clients.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.name} ${c.phone}`}
                            onSelect={() => {
                              update("client_id")({ target: { value: c.id } });
                              setClientOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                form.client_id === c.id ? "opacity-100" : "opacity-0"
                              }`}
                            />
                            {c.name} · {c.phone}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </Section>

        <Section title="Team">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {team.length === 0 && <div className="text-sm text-zinc-500">No team members yet.</div>}
            {team.map((u) => (
              <label key={u.id} className="flex items-center gap-2 p-2 rounded-md border border-black/10 hover:bg-black/[0.02] cursor-pointer">
                <Checkbox
                  data-testid={`booking-employee-${u.id}`}
                  checked={form.assigned_employee_ids.includes(u.id)}
                  onCheckedChange={(v) => {
                    const set = new Set(form.assigned_employee_ids);
                    if (v) set.add(u.id); else set.delete(u.id);
                    setForm({ ...form, assigned_employee_ids: Array.from(set) });
                  }}
                />
                <div>
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-zinc-500 capitalize">{u.role}</div>
                </div>
              </label>
            ))}
          </div>
        </Section>

        {customFields.length > 0 && (
          <Section title="Event details">
            {customFields.map((cf) => (
              <CustomFieldInput key={cf.id} field={cf} value={form.custom_data[cf.name]} onChange={updateCD(cf.name)} />
            ))}
          </Section>
        )}

        <Section title="Notes">
          <div className="space-y-3">
            <Textarea rows={3} value={form.special_instructions} onChange={update("special_instructions")} placeholder="Special instructions, prep notes…" data-testid="booking-instructions-input" />
            <Textarea rows={2} value={form.client_note} onChange={update("client_note")} placeholder="Client note (optional)" data-testid="booking-client-note-input" />
          </div>
        </Section>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate("/app/bookings")} data-testid="booking-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full px-6" data-testid="booking-submit-btn">
            {saving ? "Saving…" : editId ? "Update booking" : "Save booking"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function CustomFieldInput({ field, value, onChange }) {
  const common = { id: field.id, "data-testid": `cf-${field.name.replace(/\s+/g, "-").toLowerCase()}` };
  const [uploading, setUploading] = React.useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ file_id: data.file_id, filename: data.filename, content_type: data.content_type, size: data.size });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>{field.name}{field.required && " *"}{field.show_on_invoice && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#D85C38]">Shown on invoice</span>}</Label>
      {field.field_type === "long_text" ? (
        <Textarea rows={3} className="mt-1.5" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : field.field_type === "number" ? (
        <Input type="number" className="mt-1.5 h-11" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : field.field_type === "date" ? (
        <Input type="date" className="mt-1.5 h-11" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />
      ) : field.field_type === "dropdown" || field.field_type === "radio" ? (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="mt-1.5 h-11" data-testid={common["data-testid"]}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : field.field_type === "checkbox" ? (
        <div className="mt-1.5 flex items-center gap-2">
          <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} {...common} />
          <span className="text-sm text-zinc-600">Yes</span>
        </div>
      ) : field.field_type === "file" ? (
        <div className="mt-1.5">
          <label className="cursor-pointer inline-flex items-center px-4 h-10 rounded-md border border-black/10 bg-white text-sm hover:bg-black/[0.03]">
            <input type="file" className="hidden" onChange={onFile} data-testid={common["data-testid"]} />
            {uploading ? "Uploading…" : value?.filename ? "Replace file" : "Upload file"}
          </label>
          {value?.filename && (
            <div className="text-xs text-zinc-600 mt-2">Attached: {value.filename}</div>
          )}
        </div>
      ) : (
        <Input className="mt-1.5 h-11" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />
      )}
      {field.help_text && <div className="text-xs text-zinc-500 mt-1">{field.help_text}</div>}
    </div>
  );
}
